import Link from "next/link";
import { IRoamingInternationalCountry } from "@nju/data-access-portal-dashboard";
import { Combobox, ErrorMessage } from "@nju/form";
import { portalRoutes } from "@nju/routes";
import { A, Separator, Text } from "@nju/ui";
import { ComboboxSuffix } from "./ComboboxSuffix";

interface IChooseCountryProps {
  userId: string | undefined;
  formError: string | undefined;
  filteredCountries: IRoamingInternationalCountry[];
  handleSelectCountry: (
    selectedCountries: IRoamingInternationalCountry[],
  ) => void;
  handleClearCountry: () => void;
  inputValue: string;
  isCountrySelected: boolean;
  isLoading: boolean;
  selectedCountry: IRoamingInternationalCountry | undefined;
  setInputValue: (value: string) => void;
}

export function ChooseCountry({
  userId,
  isLoading,
  isCountrySelected,
  selectedCountry,
  inputValue,
  setInputValue,
  filteredCountries,
  formError,
  handleSelectCountry,
  handleClearCountry,
}: IChooseCountryProps) {
  return (
    <>
      <Text
        size={{ "@onlyMobile": "keyS", "@fromTablet": "keyM" }}
        weight="bold"
      >
        pakiety roamingowe
      </Text>
      <Separator size="s" tone="invisible" />
      <Text
        size={{
          "@onlyMobile": "m",
          "@fromTablet": "l",
        }}
      >
        Wybierz kraj, z którego chcesz dzwonić i w którym chcesz korzystać z
        internetu.
      </Text>
      <Separator
        size={{
          "@onlyMobile": "s",
          "@fromTablet": "xs",
        }}
        tone="invisible"
      />
      <Link
        href={portalRoutes.mojeKonto.uslugiDodatkowe.roaming.strefy.get({
          ...(userId && { userId }),
        })}
        passHref={true}
      >
        <A display="inline" size="inline" variant="neutral">
          <Text withUnderline={true}>zobacz strefy roamingowe</Text>
        </A>
      </Link>
      <Separator
        size={{
          "@fromTablet": "xs",
          "@onlyMobile": "s",
        }}
        tone="invisible"
      />
      <Combobox
        defaultValue={selectedCountry ? [selectedCountry] : undefined}
        inputDisplayValue={([value]) => {
          return value?.displayName.pl || "";
        }}
        itemComparator={(first, second) => {
          return first.displayName.pl === second.displayName.pl;
        }}
        items={inputValue?.length >= 3 ? filteredCountries : []}
        label="kraj"
        listColorVariant="gray"
        listItemRenderer={({ item }) => {
          return (
            <div className="flex items-center gap-2">
              {item?.flagImage?.url && (
                <img
                  alt="img"
                  className="w-[24px] h-[24px]"
                  src={item.flagImage.url}
                />
              )}
              {!item?.flagImage?.url && (
                <img
                  alt="not-found"
                  className="w-[24px] h-[24px] rounded-full"
                  src="/images/not_found.png"
                />
              )}
              <div key={item.displayName.pl}>
                <Text weight="bold">{item.displayName.pl}</Text>
                <Separator size="none" tone="invisible" />
                {
                  item.zones.find((zone) => {
                    return !zone.isPromo;
                  })?.displayName.pl
                }
              </div>
            </div>
          );
        }}
        onInputChange={setInputValue}
        onSelect={handleSelectCountry}
        state={formError ? "error" : undefined}
        suffix={({ reset }) => {
          return (
            <ComboboxSuffix
              isCountrySelected={!!selectedCountry}
              isLoading={isLoading}
              onClearCountry={() => {
                handleClearCountry();
                reset();
              }}
            />
          );
        }}
        testId="roaming-choose-country-input"
      />
      <Separator size="xxs" tone="invisible" />
      {formError && <ErrorMessage message={formError} />}
      {isCountrySelected && <Separator size="xl" tone="complementary" />}
    </>
  );
}
